import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import NavigationBars from '../../components/HOC/withNavigationBars';
import { Alert, Loader, Tabs, Breadcrumbs, Dropdown, Button, colors } from 'kangarootime-ui';
import { TextField, Fade } from '@material-ui/core';
import timeOffActions from './timeOffDucks/actions';
import EmployeeSchedulesTable from './Tables/EmployeeSchedules';
import EmployeeBreaksTable from './Tables/EmployeeBreaks';
import WeekControl from './WeekControl';
import moment from 'moment';
import ScheduleModal from './ScheduleModal';
import CopyShiftDialog from './Dialogs/CopyShift';
import CopySchedulesDialog from './Dialogs/CopySchedulesDialog';
import { generateTable, generatePrintableTable, exportTableToCSV } from '../../utils/print/table';
import IconToolbar from './IconToolbar';
import _ from 'lodash';
import RoomViewTable from './Tables/RoomViewTable';
import TableExports from './utilities/exports';
import TimeOffOverview from './Tables/TimeOffOverview';
import RaisedContainer from '../../components/RaisedContainer';
import DeleteDayOrWeekDialog from './Dialogs/DeleteDayOrWeekDialog/DeleteDayOrWeekDialog';
import NewTimeOffRequestForEmployeeByManager from './Dialogs/NewTimeOffRequestForEmployeeByManager';
import { capitalize } from '../../utils/string';
import schedulingActions from './schedulingDucks/actions';

// schedule statuses
const SCHEDULE_PUBLISHED = 'PUBLISHED';
const SCHEDULE_DRAFT = 'DRAFT';

class Scheduling extends Component {
  constructor (props) {
    super(props);

    this.state = {
      date: new Date(),
      activeEmployeeFilter: 'all',
      filteredEmployees: [],
      employeeSearchFilter: '',
      activeTabView: 'roomView',
      showCreateShiftModal: false,
      showCopyScheduleDialog: false,
      showCopyShiftDialog: false,
      selectedEmployee: null,
      selectedRoomForShift: null,
      createScheduleDate: null,
      shiftToPerformActionOn: null,
      shiftsInVisibleWeek: [], // keep track of the schedules in the visible week
      timeOffRequestsInVisibleWeek: [],
      shiftsGroupedByDayInVisibleWeek: {},
      showAlert: false,
      alertType: 'success',
      alertTitle: '',
      alertMessage: '',
      timeOffRequestTimeframeStart: '',
      timeOffRequestTimeframeEnd: '',
      showDeleteDayOrWeekDialog: false,
      showManagerEmployeeTimeOffRequestDialog: false
    };
  }

  get breadcrumbs () {
    return [
      { label: 'Scheduling', link: null }
    ];
  }

  get employeesViewActive () {
    return this.state.activeTabView === 'employees';
  }

  get breaksViewActive () {
    return this.state.activeTabView === 'breaks';
  }

  get timeOffViewActive () {
    return this.state.activeTabView === 'timeOffRequests';
  }

  get roomsViewActive () {
    return this.state.activeTabView === 'roomView';
  }

  get startOfWeek () {
    return moment(this.state.date).startOf('week');
  }

  get endOfWeek () {
    return moment(this.state.date).endOf('week');
  }

  get startOfWeekFormatted () {
    return this.startOfWeek.format('YYYY-MM-DD');
  }

  get endOfWeekFormatted () {
    return this.endOfWeek.format('YYYY-MM-DD');
  }

  componentDidMount = () => {
    // if we already have schedules fetched, we need to update the `__InVisibleWeek` state props so the breaks and room view table are still populated when coming back from a different page
    if (this.props.currentScheduleId) {
      const currentWeekShifts = this.props.scheduleForCurrentWeek ? this.props.scheduleForCurrentWeek.shifts : [];

      this.setState({
        shiftsInVisibleWeek: this.getObjectsInDateRange(this.startOfWeek, this.endOfWeek, currentWeekShifts),
        timeOffRequestsInVisibleWeek: this.getObjectsInDateRange(this.startOfWeek, this.endOfWeek, this.props.timeOffRequests)
      }, () => {
        this.setState({
          shiftsGroupedByDayInVisibleWeek: this.groupShiftsByDayByRoom(this.state.shiftsInVisibleWeek)
        });
      });
    }

    this.props.fetchEmployees();
    this.props.fetchRooms();
    this.props.fetchTimeOffRequests();

    this.initializeShiftsGroupedByDayArray();

    this.getScheduleForCurrentWeek();

    this.setState({ filteredEmployees: this.props.employees });
  }

  componentDidUpdate = (prevProps, prevState) => {
    // initial employees fetch was successful
    if (prevProps.employees.length === 0 && this.props.employees.length > 0) {
      this.setState({ filteredEmployees: this.props.employees });
    }

    // received room information from the api but the object hasn't been initialized
    if (!_.isEqual(prevProps.rooms, this.props.rooms)) {
      this.initializeShiftsGroupedByDayArray();
    }

    if (prevProps.timeOffRequests.length === 0 && this.props.timeOffRequests.length > 0) {
      this.setState({ timeOffRequestsInVisibleWeek: this.getObjectsInDateRange(this.startOfWeek, this.endOfWeek, this.props.timeOffRequests) });
    }

    if (prevState.date !== this.state.date) {
      // fire network request
      this.getScheduleForCurrentWeek();
    }

    // filter employee list if any of the active filters change
    if (
      (prevState.activeEmployeeFilter !== this.state.activeEmployeeFilter) ||
      (prevState.employeeSearchFilter !== this.state.employeeSearchFilter)
    ) {
      this.filterEmployees();
    }

    // whenever the current schedule id changes in redux OR the number of shifts change for the provided week changes, update state
    if (
      (prevProps.currentScheduleId !== this.props.currentScheduleId && Boolean(this.props.currentScheduleId)) ||
      (prevProps.scheduleForCurrentWeek && this.props.scheduleForCurrentWeek && prevProps.scheduleForCurrentWeek.shifts.length !== this.props.scheduleForCurrentWeek.shifts.length) ||
      (prevProps.scheduleForCurrentWeek && this.props.scheduleForCurrentWeek && _.differenceWith(prevProps.scheduleForCurrentWeek.shifts, this.props.scheduleForCurrentWeek.shifts, _.isEqual).length !== 0) ||
      (prevProps.schedulesNetworkAction && !this.props.schedulesNetworkAction)
    ) {
      const currentWeekShifts = this.props.scheduleForCurrentWeek ? this.props.scheduleForCurrentWeek.shifts : [];

      this.setState({
        shiftsInVisibleWeek: this.getObjectsInDateRange(this.startOfWeek, this.endOfWeek, currentWeekShifts),
        timeOffRequestsInVisibleWeek: this.getObjectsInDateRange(this.startOfWeek, this.endOfWeek, this.props.timeOffRequests)
      }, () => {
        this.setState({
          shiftsGroupedByDayInVisibleWeek: this.groupShiftsByDayByRoom(this.state.shiftsInVisibleWeek)
        });
      });
    }

    if (prevProps.copyingShift && !this.props.copyingShift) {
      this.setState({ showCopyShiftDialog: false });
    }

    if (!prevProps.schedulingError && this.props.schedulingError) {
      this.showAlert('danger', 'Scheduling Error', this.props.schedulingError);
    }

    // made a scheduling network action, dismiss any modals if any are visible and show alert
    if (prevProps.schedulesNetworkAction && !this.props.schedulesNetworkAction) {
      const hasNewError = !prevProps.schedulingError && this.props.schedulingError;

      if (hasNewError) {
        this.showAlert('danger', 'Error', `${this.props.schedulingError || 'Something went wrong.'}`);
      } else {
        if (
          this.state.showCopyScheduleDialog ||
          this.state.showCopyShiftDialog ||
          this.state.showCreateShiftModal ||
          this.state.showDeleteDayOrWeekDialog
        ) {
          this.showAlert('success', 'Success!', '');
        }
      }
    }

    // manager made a time off request for an employee, dismiss modals
    if (prevProps.timeOffRequestNetworkAction && !this.props.timeOffRequestNetworkAction) {
      if (this.state.showManagerEmployeeTimeOffRequestDialog) {
        this.setState({ showManagerEmployeeTimeOffRequestDialog: false });
      }
    }

    // ensure the selected shift object in state is reset when the copy shift modal is dismissed
    if (prevState.showCopyShiftDialog && !this.state.showCopyShiftDialog) {
      if (this.state.shiftToPerformActionOn) {
        this.setState({ shiftToPerformActionOn: null });
      }
    }
  }

  /**
   * Filter array of objects in the defined range
   * @param {Date} startDate
   * @param {Date} endDate
   * @returns {Array}
   */
  getObjectsInDateRange = (startDate, endDate, arr) => {
    let copy = [...arr];

    return copy.filter(obj => {
      const { start } = obj;

      return moment(start).isBetween(moment(startDate), moment(endDate));
    });
  }

  groupShiftsByDayByRoom = shifts => {
    const { rooms } = this.props;
    const roomIds = rooms.map(room => room.id);

    // each 'day' object contains all room ids which contain an array of shifts that day
    let week = {
      monday: {},
      tuesday: {},
      wednesday: {},
      thursday: {},
      friday: {}
    };

    if (roomIds.length) {
      Object.keys(week).forEach(key => {
        // turn array of values into an object where the key is a value from the array and its value is an empty array
        week[key] = Object.assign(...roomIds.map(id => ({ [id]: [] })));
      });

      shifts.forEach(shift => {
        const { roomId, start } = shift;
        const dayOfWeek = moment(start).format('dddd').toLowerCase();

        if (week[dayOfWeek]) {
          week[dayOfWeek][roomId].push(shift);
        }
      });
    }

    return week;
  }

  // current visible dates in table
  visibleDates = () => {
    const { date } = this.state;
    const start = moment(date).startOf('week');
    const end = moment(date).endOf('week');
    let currentDate = start;

    let dates = [];

    // need to use 'valueOf' on moment objects since semistandard says 'currentDate'/'end' is not modified in this loop
    while (currentDate.valueOf() <= end.valueOf()) {
      // don't add sunday and saturday
      if (currentDate.day() > 0 && currentDate.day() < 6) {
        const dateToAdd = currentDate.toDate();
        dates.push(dateToAdd);
      }

      currentDate.add('days', 1);
    }

    return dates;
  }

  /**
   * determine if the current schedule can be copied to another week and or cleared
   */
  allowedActions = () => {
    const { scheduleForCurrentWeek } = this.props;
    const dateRange = this.visibleDates();
    const { 0: firstDateInRange } = dateRange;

    // only PUBLISHED schedules can be copied
    let canCopyWeekSchedule = scheduleForCurrentWeek && scheduleForCurrentWeek.publishStatus === SCHEDULE_PUBLISHED;
    let canClearCurrentWeekSchedule = false;

    // check if the visible week can be cleared. cannot be a week in the past and the schedule must be a DRAFT
    if (scheduleForCurrentWeek && scheduleForCurrentWeek.publishStatus === SCHEDULE_DRAFT) {
      if (moment(firstDateInRange).isAfter(moment().startOf('week'))) {
        canClearCurrentWeekSchedule = true;
      }
    }

    return { canCopyWeekSchedule, canClearCurrentWeekSchedule };
  }

  // determine what action buttons should be visible in the table's toolbar
  visibleActionButtons = () => {
    const { canCopyWeekSchedule, canClearCurrentWeekSchedule } = this.allowedActions();
    let actionButtons = [];

    if (!this.timeOffViewActive) {
      actionButtons.push({ faIcon: 'fa-external-link-alt', tooltip: 'Export', onClick: this.handleExportTableAction });
      actionButtons.push({ faIcon: 'fa-print', tooltip: 'Print', onClick: this.handlePrintTableAction });
    }

    if (canCopyWeekSchedule && this.roomsViewActive) {
      actionButtons.push({ faIcon: 'fa-copy', tooltip: 'Copy Week', onClick: () => this.setState({ showCopyScheduleDialog: true }) });
    }

    if (canClearCurrentWeekSchedule && this.roomsViewActive) {
      actionButtons.push({ faIcon: 'fa-times-circle', tooltip: 'Delete', onClick: () => this.setState({ showDeleteDayOrWeekDialog: true }) });
    }

    return actionButtons;
  }

  changeWeek = numberOfWeeks => {
    const { date } = this.state;
    const momentDate = moment(date).add(numberOfWeeks, 'weeks');
    const toDate = momentDate.toDate();

    this.setState({ date: toDate });
  }

  leftAreaWeekControlComponent = () => {
    const { schedulesNetworkAction } = this.props;

    if (this.employeesViewActive || this.breaksViewActive) {
      return (
        <div style={{ width: 120 }}>
          <Dropdown
            value={capitalize(this.state.activeEmployeeFilter)}
            handleSelection={newValue => this.setState({ activeEmployeeFilter: newValue.toLowerCase() })}
            options={['All', 'Active', 'Inactive', 'Invited']}
          />
        </div>
      );
    } else if (this.roomsViewActive) {
      const canCreateNewSchedule = !this.currentWeekHasSchedule();
      const weekHasPassed = moment(this.startOfWeek).isBefore(moment().startOf('week'));

      /**
       * when to show buttons
       * New - week does not have a schedule associated to it
       * Save - week has a schedule associated to it and is in state 'DRAFT'
       * Publish - week has a schedule associated to it and is in state 'DRAFT'
       * Unpublish - week has a schedule associated to it and is in state 'PUBLISHED'
       */
      return (
        <Fade in={!schedulesNetworkAction}>
          <div>
            { this.props.scheduleForCurrentWeek && <h5>STATUS: <span style={{ transform: 'uppercase', color: this.props.scheduleForCurrentWeek.publishStatus === 'PUBLISHED' ? colors.success : colors.danger }}>{this.props.scheduleForCurrentWeek.publishStatus}</span></h5> }
            { !weekHasPassed
              ? <div className='flex flex-row align-items-center'>
                { canCreateNewSchedule
                  ? <Button type='ghost' onClick={this.createNewScheduleForVisibleWeek}>New</Button>
                  : <Fragment>
                    { this.props.scheduleForCurrentWeek.publishStatus === SCHEDULE_DRAFT &&
                      (this.props.scheduleForCurrentWeek.shifts.length > 0 || (this.props.shiftsToRemoveByScheduleId[this.props.scheduleForCurrentWeek.scheduleId] && this.props.shiftsToRemoveByScheduleId[this.props.scheduleForCurrentWeek.scheduleId].length)) &&
                      <Fragment>
                        <Button type='secondary' onClick={this.saveSchedule}>Save</Button>
                        <Button disabled={this.scheduleForCurrentWeekHasUnsavedChanges()} onClick={this.publishSchedule}>Publish</Button>
                      </Fragment>
                    }
                    { this.props.scheduleForCurrentWeek.publishStatus === SCHEDULE_PUBLISHED && <Button onClick={this.unpublishSchedule}>Unpublish</Button> }
                  </Fragment>
                }
              </div>
              : null
            }
          </div>
        </Fade>
      );
    }

    return null;
  }

  handleCreateScheduleRequest = (employee, date, room) => {
    if (employee) {
      this.setState({
        showCreateShiftModal: true,
        selectedEmployee: employee,
        createScheduleDate: date,
        selectedRoomForShift: room
      });
    }
  }

  createSchedule = (employeeId, roomId, startTime, endTime, breakMinutes) => {
    const shift = {
      employeeId,
      roomId,
      start: startTime,
      end: endTime,
      startDate: startTime,
      endDate: endTime,
      breakMinutes
    };

    if (this.props.scheduleForCurrentWeek) {
      this.props.saveShiftToWeekSchedule(shift, this.props.scheduleForCurrentWeek.scheduleId);
    }

    this.setState({ showCreateShiftModal: false });
  }

  handleEditSchedule = (employee, schedule, selectedRoomInformation) => {
    this.setState({
      showCreateShiftModal: true,
      selectedEmployee: employee,
      createScheduleDate: moment(schedule.start),
      shiftToPerformActionOn: schedule,
      selectedRoomForShift: selectedRoomInformation
    });
  }

  editShift = (employeeId, newRoomId, newStartTime, newEndTime, breakMinutes) => {
    const { shiftToPerformActionOn } = this.state;

    if (!shiftToPerformActionOn) {
      return;
    }

    // use id since shift exists
    const { id } = shiftToPerformActionOn;
    const shift = {
      id,
      roomId: newRoomId,
      start: newStartTime,
      end: newEndTime,
      employeeId,
      breakMinutes
    };

    if (this.props.currentScheduleId) {
      this.props.editExistingShift(shift, this.props.currentScheduleId);
    }

    this.setState({ showCreateShiftModal: false, selectedEmployee: null, shiftToPerformActionOn: null, selectedRoomForShift: null });
  }

  handlePrintTableAction = () => {
    const { activeTabView } = this.state;
    let table;

    switch (activeTabView) {
      case 'employees': {
        table = this.generateSchedulesHTMLTable();
        break;
      }
      case 'breaks': {
        table = this.generateBreaksHTMLTable();
        break;
      }
      case 'roomView': {
        table = this.generateRoomsViewHTMLTable();
        break;
      }
      default:
    }

    if (table) {
      const printableTable = generatePrintableTable(table);

      this.printTable(printableTable);
    }
  }

  handleExportTableAction = () => {
    const { activeTabView } = this.state;
    const visibleDates = this.visibleDates();
    const { 0: first } = visibleDates;
    const month = moment(first).format('MMM');
    const day = moment(first).format('D');
    let table;
    let filename = `week_of_${month}_${day}`;

    switch (activeTabView) {
      case 'employees': {
        table = this.generateSchedulesHTMLTable();
        filename = `${filename}_schedules.csv`;
        break;
      }
      case 'breaks': {
        table = this.generateBreaksHTMLTable();
        filename = `${filename}_breaks.csv`;
        break;
      }
      case 'roomView': {
        table = this.generateRoomsViewHTMLTable();
        filename = `${filename}_room_view.csv`;
        break;
      }
      default:
        break;
    }

    if (table) {
      // convert table html string into its own html document
      const htmlTable = new DOMParser().parseFromString(table, 'text/html');

      exportTableToCSV(htmlTable, filename);
    }
  }

  generateSchedulesHTMLTable = () => {
    const { roomsById } = this.props;
    const formattedDates = this.visibleDates().map(date => moment(date).format('dddd - MMM Do'));
    const columns = ['Name', ...formattedDates, 'Hours'];
    let shiftsCopy = [...this.state.shiftsInVisibleWeek];

    shiftsCopy = shiftsCopy.map(shift => ({
      ...shift,
      roomName: roomsById[shift.roomId] || 'Floats'
    }));

    const rows = TableExports.schedules(this.props.employeesById, shiftsCopy);

    return generateTable(columns, rows);
  }

  generateBreaksHTMLTable = () => {
    const formattedDates = this.visibleDates().map(date => moment(date).format('dddd - MMM Do'));
    const columns = ['Name', ...formattedDates];
    const rows = TableExports.breaks(this.props.employeesById, this.state.shiftsInVisibleWeek);

    return generateTable(columns, rows);
  }

  generateRoomsViewHTMLTable = () => {
    const formattedDates = this.visibleDates().map(date => moment(date).format('dddd - MMM Do'));
    const columns = ['Room', ...formattedDates];
    const rows = TableExports.roomView(
      this.props.employeesById,
      this.state.shiftsGroupedByDayInVisibleWeek,
      this.props.roomsById,
      this.props.roomAttendanceSchedules,
      this.props.roomRatios
    );

    return generateTable(columns, rows);
  }

  printTable = tableContents => {
    const newWindow = window.open('', '_blank');

    newWindow.document.write(tableContents);
    newWindow.print();
  }

  showAlert = (type, title, message) => {
    // scroll to the top of the page so the user can see any alert that is shown
    window.scrollTo(0, 0);

    // set alert contents and hide any dialogs
    this.setState({
      showAlert: true,
      alertType: type,
      alertTitle: title,
      alertMessage: message,
      showCreateShiftModal: false,
      showCopyScheduleDialog: false,
      showCopyShiftDialog: false,
      shiftToPerformActionOn: null,
      selectedEmployee: null
    }, () => {
      setTimeout(() => {
        this.setState({
          showAlert: false,
          alertType: '',
          alertTitle: '',
          alertMessage: ''
        });
      }, 4500);
    });
  }

  // debounce filtering the table until user stops typing
  setEmployeeSearchText = _.debounce(text => {
    if (this.props.employees) {
      this.setState({ employeeSearchFilter: text });
    }
  }, 500);

  filterEmployees = () => {
    // filter filtered employees for this name
    let filteredEmployeesCopy = [...this.props.employees];

    if (this.state.activeEmployeeFilter === 'all') {
      // nest the reset
      if (this.state.employeeSearchFilter === '') {
        this.setState({ filteredEmployees: this.props.employees });
      }
    } else {
      // filter employee by status; 'Active', 'Inactive', etc.
      filteredEmployeesCopy = filteredEmployeesCopy.filter(employee => employee.status === this.state.activeEmployeeFilter);
    }

    // if there is a search input, filter the employees some more
    if (this.state.employeeSearchFilter) {
      const regex = new RegExp(this.state.employeeSearchFilter.toLowerCase());

      filteredEmployeesCopy = filteredEmployeesCopy.filter(employee => regex.test(employee.name.toLowerCase()));
    }

    this.setState({ filteredEmployees: filteredEmployeesCopy });
  }

  /**
   * fetch all employees with an all day approved time off request on date for `this.state.createScheduleDate`
   *
   * @returns {Object}
   */
  employeesWithApprovedAllDayTimeOff = () => {
    if (!this.state.createScheduleDate) {
      return {};
    } else {
      const day = this.state.createScheduleDate.clone();
      /**
       * filter array of time off requests into an array containing only requests on selected date, then map over filter results and only return the employee id
       */
      const shifts = this.props.timeOffRequests.filter(req => {
        const timeOffApproved = req.status && req.status.status.match(/approve/);
        const dateFallsInRequestRange = moment(req.start).isSameOrBefore(day, 'date') && moment(req.end).isSameOrAfter(day, 'date');
        const requestFallsOnDate = moment(req.start).isSame(day, 'date');
        const employeeIsOffForDate = dateFallsInRequestRange || requestFallsOnDate;

        return (
          employeeIsOffForDate &&
          timeOffApproved &&
          req.allDay
        );
      }).map(req => req.employeeId);

      return shifts.length ? Object.assign(...shifts.map(key => ({ [key]: true }))) : {};
    }
  }

  // ensure the `shiftsGroupedByDayInVisibleWeek` array is properly initialized
  // yeah this is repeated from groupShiftsByDayByRoom
  initializeShiftsGroupedByDayArray = () => {
    const { rooms } = this.props;
    const roomIds = rooms.map(room => room.id);

    const week = {
      monday: {},
      tuesday: {},
      wednesday: {},
      thursday: {},
      friday: {}
    };

    if (roomIds.length) {
      Object.keys(week).forEach(key => {
        // turn array of values into an object where the key is a value from the array and its value is an empty array
        week[key] = Object.assign(...roomIds.map(id => ({ [id]: [] })));
      });
    }

    this.setState({ shiftsGroupedByDayInVisibleWeek: week });
  }

  deleteShiftsForSelectedDates = dates => {
    // filter array of shifts for the selected week based on if its start time is the same date as one of the provided selected dates
    const shiftsToDelete = this.state.shiftsInVisibleWeek.filter(shift => dates.some(date => moment(shift.start).isSame(date, 'date')));

    if (shiftsToDelete.length) {
      this.removeShiftsFromSchedule(shiftsToDelete);
    }

    this.setState({ showDeleteDayOrWeekDialog: false });
  }

  /// shifts belonging to a schedule functions
  getScheduleForCurrentWeek = () => {
    const { schedulesIdsByWeek } = this.props;

    // should only execute this if the current week is not in redux
    if (schedulesIdsByWeek[this.startOfWeekFormatted]) {
      const scheduleId = schedulesIdsByWeek[this.startOfWeekFormatted];

      this.props.setCurrentWeekScheduleId(scheduleId);
    } else {
      this.props.getScheduleForCurrentWeek(this.startOfWeekFormatted, this.endOfWeekFormatted);
    }
  }
  createNewScheduleForVisibleWeek = () => {
    this.props.createNewSchedule(this.startOfWeekFormatted, this.endOfWeekFormatted);
  }

  currentWeekHasSchedule = () => {
    return Boolean(this.props.scheduleForCurrentWeek);
  }

  currentWeekScheduleIsPublished = () => {
    if (this.props.scheduleForCurrentWeek) {
      return this.props.scheduleForCurrentWeek.publishStatus === SCHEDULE_PUBLISHED;
    }

    return false;
  }

  saveSchedule = () => {
    if (this.props.scheduleForCurrentWeek) {
      const shiftsToRemove = this.props.shiftsToRemoveByScheduleId[this.props.scheduleForCurrentWeek.scheduleId] || [];

      this.props.saveSchedule(this.props.scheduleForCurrentWeek.shifts, shiftsToRemove, this.props.scheduleForCurrentWeek.scheduleId);
    }
  }

  publishSchedule = () => {
    if (this.props.scheduleForCurrentWeek) {
      this.props.publishSchedule(this.props.scheduleForCurrentWeek.scheduleId);
    }
  }

  unpublishSchedule = () => {
    if (this.props.scheduleForCurrentWeek) {
      this.props.unpublishSchedule(this.props.scheduleForCurrentWeek.scheduleId);
    }
  }

  // take shifts array for the current week's schedule and convert into an object where the keys are employee ids and the values are an array of shift objects
  schedulesByEmployeeIdForCurrentSchedule = () => {
    if (this.props.scheduleForCurrentWeek) {
      const { roomsById } = this.props;
      const { shifts } = this.props.scheduleForCurrentWeek;
      const byEmployeeId = {};

      shifts.forEach(shift => {
        const { employeeId, start } = shift;
        const key = moment(start).format('YYYY-MM-DD');

        // add room name to the shift information
        const shiftWithRoomName = {
          ...shift,
          roomName: roomsById[shift.roomId] || 'Floats'
        };

        if (byEmployeeId[employeeId]) {
          if (byEmployeeId[employeeId][key]) {
            byEmployeeId[employeeId][key].push(shiftWithRoomName);
          } else {
            byEmployeeId[employeeId][key] = [shiftWithRoomName];
          }
        } else {
          byEmployeeId[employeeId] = {
            [key]: [shiftWithRoomName]
          };
        }
      });

      return byEmployeeId;
    }

    return {};
  }

  /**
   * @param {Object[]} shifts
   */
  removeShiftsFromSchedule = shifts => {
    if (this.props.currentScheduleId) {
      this.props.removeShiftsFromSchedule(shifts, this.props.currentScheduleId);
    }
  }

  /**
   * control whether to show the new shift button or not
   * @returns {Boolean}
   */
  canCreateNewShift = () => {
    return (
      this.currentWeekHasSchedule() &&
      this.props.scheduleForCurrentWeek.publishStatus === SCHEDULE_DRAFT &&
      !this.props.schedulesNetworkAction
    );
  }

  copyScheduleToWeek = (startDate, endDate) => {
    if (this.props.currentScheduleId) {
      this.props.copyScheduleToNewWeek(this.props.currentScheduleId, startDate, endDate);
    }
  }

  scheduleForCurrentWeekHasUnsavedChanges = () => {
    if (this.props.currentScheduleId) {
      const shiftsToRemoveFromSchedule = this.props.shiftsToRemoveByScheduleId[this.props.currentScheduleId];
      const shiftsForCurrentSchedule = this.props.scheduleForCurrentWeek ? this.props.scheduleForCurrentWeek.shifts : [];
      const hasDraftShifts = shiftsForCurrentSchedule.some(shift => shift.id.toString().includes('__DRAFT'));

      return (shiftsToRemoveFromSchedule && shiftsToRemoveFromSchedule.length > 0) || hasDraftShifts;
    }

    return false;
  }
  /// end of shfits belonging to a schedule functions

  render () {
    const { date, showCreateShiftModal, selectedEmployee, createScheduleDate, shiftToPerformActionOn, showCopyScheduleDialog, activeTabView, filteredEmployees } = this.state;

    return (
      <NavigationBars>
        <Loader loading={false}>
          <div className='flex-columns flex-1'>
            {this.state.showAlert &&
              <div className='flex' style={{ justifyContent: 'center' }}>
                <Alert type={this.state.alertType} title={this.state.alertTitle}>{this.state.alertMessage}</Alert>
              </div>
            }
            <Breadcrumbs breadcrumbs={this.breadcrumbs} />
            <div className='flex-rows'>
              <div className='flex-1'>
                <Tabs
                  tabs={[
                    { label: 'Center', value: 'roomView' },
                    { label: 'Employees', value: 'employees' },
                    { label: 'Breaks', value: 'breaks' },
                    { label: 'Time Off', value: 'timeOffRequests', badgeCount: this.props.pendingTimeOffRequests.length > 0 && this.props.pendingTimeOffRequests.length }
                  ]}
                  activeTabView={activeTabView}
                  handleTabChange={value => this.setState({ activeTabView: value })}
                />
              </div>
            </div>
            <RaisedContainer animated={false}>
              <div id='employee-scheduling-table' style={{ margin: 16 }}>
                {!this.timeOffViewActive &&
                  <div>
                    <WeekControl
                      date={date}
                      onNextWeek={() => this.changeWeek(1)}
                      onPreviousWeek={() => this.changeWeek(-1)}
                      rightAreaComponent={
                        <Fragment>
                          <IconToolbar icons={this.visibleActionButtons()} />
                          <TextField
                            placeholder='Search'
                            onChange={event => this.setEmployeeSearchText(event.target.value)}
                            disabled={!!this.timeOffViewActive || !!this.roomsViewActive}
                          />
                        </Fragment>
                      }
                      leftAreaComponent={this.leftAreaWeekControlComponent()}
                    />
                  </div>
                }
                { activeTabView === 'employees' && <EmployeeSchedulesTable
                  employees={filteredEmployees}
                  employeeSchedules={this.schedulesByEmployeeIdForCurrentSchedule()}
                  isLoading={this.props.schedulesNetworkAction}
                  visibleDates={this.visibleDates()}
                  employeeTimeOffRequests={this.props.timeOffRequestsByEmployeeId}
                />
                }
                {activeTabView === 'breaks' && <EmployeeBreaksTable
                  employees={filteredEmployees}
                  employeeSchedules={this.schedulesByEmployeeIdForCurrentSchedule()}
                  visibleDates={this.visibleDates()}
                  shiftsThisWeek={this.state.shiftsInVisibleWeek}
                />
                }
                {activeTabView === 'timeOffRequests' && <TimeOffOverview
                  employeesById={this.props.employeesById}
                  timeOffRequests={this.props.timeOffRequests}
                  timeOffRequestsByEmployeeId={this.props.timeOffRequestsByEmployeeId}
                  updateTimeOffRequestStatus={(id, status, hoursApproved) => this.props.updateTimeOffRequestStatus(id, status, hoursApproved)}
                  editRequest={this.props.directorEditRequest}
                  currentCenterId={this.props.currentCenterId}
                  archiveRequest={this.props.archiveTimeOffRequest}
                  isLoading={this.props.fetchingTimeOffRequests}
                  searchTimeframeStart={this.state.timeOffRequestTimeframeStart}
                  searchTimeframeEnd={this.state.timeOffRequestTimeframeEnd}
                  searchForRequestsInTimeframe={() => this.props.fetchTimeOffRequestsInRange(this.state.timeOffRequestTimeframeStart, this.state.timeOffRequestTimeframeEnd)}
                  setTimeOffRequestSearchTimeframe={(time, key) => this.setState({ [key]: time })}
                  showManagerEmployeeTimeOffRequestDialog={() => this.setState({ showManagerEmployeeTimeOffRequestDialog: true })}
                />
                }
                { activeTabView === 'roomView' && <RoomViewTable
                  shiftsGroupedByDay={this.state.shiftsGroupedByDayInVisibleWeek}
                  employeesById={this.props.employeesById}
                  roomsById={this.props.roomsById}
                  projectedRoomAttendance={this.props.roomAttendanceSchedules}
                  roomRatios={this.props.roomRatios}
                  // isLoading={this.props.schedulesNetworkAction}
                  datesInVisibleWeek={this.visibleDates()}
                  createNewShift={this.handleCreateScheduleRequest}
                  editShift={this.handleEditSchedule}
                  copyShift={shiftToPerformActionOn => this.setState({ showCopyShiftDialog: true, shiftToPerformActionOn })}
                  deleteShift={shift => this.removeShiftsFromSchedule([shift])}
                  schedulesNetworkAction={this.props.schedulesNetworkAction}
                  hideCreateShiftButton={!this.canCreateNewShift()}
                />
                }
              </div>
            </RaisedContainer>
            {selectedEmployee
              ? <ScheduleModal
                date={createScheduleDate}
                isOpen={showCreateShiftModal}
                onClose={() => this.setState({ showCreateShiftModal: false, selectedEmployee: null, shiftToPerformActionOn: null, selectedRoomForShift: null })}
                createSchedule={this.createSchedule}
                performingAction={this.props.schedulesNetworkAction}
                shift={shiftToPerformActionOn}
                editShift={this.editShift}
                selectedRoomInformation={this.state.selectedRoomForShift}
                employeesToDisableSelection={this.employeesWithApprovedAllDayTimeOff()}
              /> : null
            }
            <CopySchedulesDialog
              isOpen={showCopyScheduleDialog}
              onClose={() => this.setState({ showCopyScheduleDialog: false })}
              performingAction={this.props.schedulesNetworkAction}
              copySchedules={this.copyScheduleToWeek}
            />
            <CopyShiftDialog
              isOpen={this.state.showCopyShiftDialog}
              dismiss={() => this.setState({ showCopyShiftDialog: false, shiftToPerformActionOn: null })}
              copyShift={dates => {
                this.setState({ showCopyShiftDialog: false });
                this.props.copyShiftToNewDates(shiftToPerformActionOn, dates);
              }}
              performingAction={this.props.copyingShift}
            />
            <DeleteDayOrWeekDialog
              isOpen={this.state.showDeleteDayOrWeekDialog}
              onClose={() => this.setState({ showDeleteDayOrWeekDialog: false })}
              startDate={this.startOfWeek}
              endDate={this.endOfWeek}
              onSubmit={this.deleteShiftsForSelectedDates}
            />
            <NewTimeOffRequestForEmployeeByManager
              isOpen={this.state.showManagerEmployeeTimeOffRequestDialog}
              onClose={() => this.setState({ showManagerEmployeeTimeOffRequestDialog: false })}
              onSubmit={this.props.createTimeOffRequestAsManagerForEmployee}
              performingAction={this.props.timeOffRequestNetworkAction}
            />
          </div>
        </Loader>
      </NavigationBars>
    );
  }
}

function mapStateToProps (state) {
  return {
    employees: state.employees.employees,
    employeesById: state.employees.byId,
    rooms: state.rooms.rooms,
    roomsById: state.rooms.roomNames,
    timeOffRequests: state.scheduling.timeOffRequests.all,
    timeOffRequestsByEmployeeId: state.scheduling.timeOffRequests.byEmployeeId,
    pendingTimeOffRequests: state.scheduling.timeOffRequests.all.filter(req => req.status === undefined && moment(req.start).isAfter(moment())),
    roomAttendanceSchedules: state.rooms.roomAttendanceSchedules,
    roomRatios: state.rooms.roomRatios,
    currentCenterId: state.identity.centerId,
    fetchingTimeOffRequests: state.scheduling.timeOffRequests.meta.fetchingTimeOffRequests,
    timeOffRequestNetworkAction: state.scheduling.timeOffRequests.meta.timeOffRequestNetworkAction,
    // scheduling with shift props
    schedulesNetworkAction: state.scheduling.schedules.meta.networkAction,
    schedulesById: state.scheduling.schedules.byId,
    schedulesIdsByWeek: state.scheduling.schedules.byWeek,
    scheduleForCurrentWeek: state.scheduling.schedules.byId[state.scheduling.schedules.currentScheduleId],
    currentScheduleId: state.scheduling.schedules.currentScheduleId,
    shiftsToRemoveByScheduleId: state.scheduling.schedules.shiftsToBeRemovedByScheduleId,
    copyingShift: state.scheduling.schedules.meta.copyingShift,
    meta: state.scheduling.schedules.meta,
    schedulingError: state.scheduling.schedules.meta.error
  };
}

function mapDispatchToProps (dispatch) {
  return {
    fetchEmployees: () => dispatch({ type: 'FETCH_EMPLOYEES' }),
    fetchRooms: () => dispatch({ type: 'FETCH_ROOMS' }),
    fetchTimeOffRequests: () => dispatch(timeOffActions.getTimeOffRequestsRequest()),
    fetchTimeOffRequestsInRange: (start, end) => dispatch(timeOffActions.getTimeOffRequestsRequest(start, end)),
    updateTimeOffRequestStatus: (id, status, hoursApproved) => dispatch(timeOffActions.updateTimeOffRequestStatusRequest(id, status, hoursApproved)),
    directorEditRequest: (id, requestType, timeOffType, start, end, description) => dispatch(timeOffActions.editRequest(id, requestType, timeOffType, start, end, description)),
    archiveTimeOffRequest: (id) => dispatch(timeOffActions.archiveTimeOffRequest(id)),
    createTimeOffRequestAsManagerForEmployee: request => dispatch(timeOffActions.managerCreateEmployeeTimeOffRequest(request)),
    // scheduling action
    createNewSchedule: (start, end) => dispatch(schedulingActions.createNewScheduleRequest(start, end)),
    getScheduleForCurrentWeek: (start, end) => dispatch(schedulingActions.getScheduleForWeekRequest(start, end)),
    setCurrentWeekScheduleId: (scheduleId) => dispatch(schedulingActions.setCurrentWeekScheduleId(scheduleId)),
    saveShiftToWeekSchedule: (shift, scheduleId) => dispatch(schedulingActions.saveShiftToWeekSchedule(shift, scheduleId)),
    saveSchedule: (shifts, shiftsToRemove = [], scheduleId) => dispatch(schedulingActions.saveScheduleRequest(shifts, shiftsToRemove, scheduleId)),
    publishSchedule: scheduleId => dispatch(schedulingActions.publishScheduleRequest(scheduleId)),
    unpublishSchedule: scheduleId => dispatch(schedulingActions.unpublishScheduleRequest(scheduleId)),
    removeShiftsFromSchedule: (shifts, scheduleId) => dispatch(schedulingActions.removeShiftsFromSchedule(shifts, scheduleId)),
    editExistingShift: (shift, scheduleId) => dispatch(schedulingActions.editExistingShift(shift, scheduleId)),
    copyScheduleToNewWeek: (scheduleId, start, end) => dispatch(schedulingActions.copyExistingScheduleToNewWeekRequest(scheduleId, start, end)),
    copyShiftToNewDates: (shift, dates) => dispatch(schedulingActions.copyShiftToNewDatesRequest(shift, dates))
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Scheduling);
